import React, { ElementType, ReactElement, useEffect, useRef } from "react";

import { Entity } from "@/jenesys-types/data/common";
import { DataType } from "@/jenesys-types/data/enums";
import { Transaction } from "@/jenesys-types/jenesys-entities/config";

import { BreadcrumbItem } from "@/components/common/breadcrumbs/BreadcrumbItem";
import { LoadingErrorContentSwitch } from "@/components/common/errors/LoadingErrorContentSwitch";
import useCrudEdit from "@/hooks/crud/useCrudEdit";
import useEntityNamings from "@/hooks/util/useEntityNamings";
import { PathParams } from "@/services/api";
import { performanceMonitor } from "@/utils/performance";

export interface CrudEditProps {
  form: ElementType;
  extraData?: Record<string, any>;
  pathParams?: PathParams;
  breadcrumbTitle?: string;
}

export function CrudEdit<E extends Entity>({
  form: Form,
  extraData,
  pathParams,
  breadcrumbTitle
}: CrudEditProps): ReactElement<any, any> | null {
  const {
    dataType,
    isLoading,
    isSaving,
    itemData,
    loadingError,
    savingError,
    onCancel,
    updateItem
  } = useCrudEdit<E>(pathParams);

  const { singular: entityName } = useEntityNamings(dataType);

  const componentInstanceId = useRef(
    `${dataType || "unknown"}-${itemData?.id || "new"}-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`
  ).current;
  const initialRenderMetricId = useRef<string | null>(null);
  const formReadyMetricId = useRef<string | null>(null);

  useEffect(() => {
    const irContext = {
      componentInstanceId,
      dataType,
      itemId: itemData?.id,
      hookState: { isLoading, hasItemData: !!itemData }
    };
    initialRenderMetricId.current = performanceMonitor.start(
      "Component.CrudEdit.initialRender",
      irContext
    );

    return () => {
      if (initialRenderMetricId.current) {
        performanceMonitor.stop(initialRenderMetricId.current, {
          unmount: true
        });
        initialRenderMetricId.current = null;
      }
      if (formReadyMetricId.current) {
        performanceMonitor.stop(formReadyMetricId.current, { unmount: true });
        formReadyMetricId.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentInstanceId]);

  useEffect(() => {
    if (initialRenderMetricId.current) {
      performanceMonitor.stop(initialRenderMetricId.current, {
        hookState: { isLoading, hasItemData: !!itemData }
      });
      initialRenderMetricId.current = null;
    }

    if (!isLoading && itemData && !formReadyMetricId.current) {
      const frContext = {
        componentInstanceId,
        dataType,
        itemId: itemData?.id,
        hookState: { isLoading, hasItemData: !!itemData }
      };
      formReadyMetricId.current = performanceMonitor.start(
        "Component.CrudEdit.formReady",
        frContext
      );
    }
  });

  useEffect(() => {
    if (formReadyMetricId.current && !isLoading && itemData) {
      performanceMonitor.stop(formReadyMetricId.current, {
        hookState: { isLoading, hasItemData: !!itemData }
      });
      formReadyMetricId.current = null;
    }
  }, [isLoading, itemData, dataType, componentInstanceId]);

  // TODO: Remove this once we move duplicates into a modal. (See https://jenesys.atlassian.net/browse/ENG-2569)
  const transactionDuplicateCheck =
    dataType === DataType.Transaction &&
    (itemData as unknown as Transaction)?.status === "DUPLICATE";

  return (
    <React.Fragment>
      <BreadcrumbItem
        title={transactionDuplicateCheck ? "" : breadcrumbTitle || "Edit"}
      />
      <LoadingErrorContentSwitch
        showContentOnError
        isLoading={isLoading}
        error={loadingError || savingError}
        errorTitle={
          loadingError
            ? `Error loading ${entityName}`
            : `Error saving ${entityName}`
        }
        showContent={!!itemData}
      >
        <Form
          isEdit
          onSave={updateItem}
          onCancel={onCancel}
          isSaving={isSaving}
          initialValues={itemData}
          extraData={extraData}
          entityName={entityName}
          dataType={dataType}
        />
      </LoadingErrorContentSwitch>
    </React.Fragment>
  );
}
